// game-options-io.ts — the pure read/apply MODEL behind the in-game GAME OPTIONS
// DOM overlay (HTML/DOM UI shell plan, Phase 5, Form 4). Mirrors the canvas
// InGameOptionsForm (gameoptions.ts:334-513) EXACTLY, against the SAME ShellEnv
// seams the canvas form used — so the DOM is presentation-only (no sim/command/
// wire change). Pure (no DOM), node-testable, and shared by the view AND the
// tests so both exercise the IDENTICAL mapping (the same no-drift discipline as
// the shell options-io.ts).
//
// Unlike the SHELL options-io (menu-view) — which has no live game and holds the
// game speed locally until OK persists it — THIS form applies to the RUNNING
// game: OK calls env.setGameSpeed() (the live sim speed), plrLocal.SetHandicap(),
// getSimUIForm().SetUIType(), and savePreferences(); Mute toggles sndm.Enable
// LIVE while the form is up. CANCEL reverts the live mute. The shell options-view
// is UNTOUCHED.

import { kcmsUpdate } from '../../io/prefs.ts';
import { setInstructionMouseMode } from '../../ui/inputverbs.ts';
import {
  kfHcapEasy,
  kfHcapNormal,
  kfHcapHard,
  kfHcapDefault,
} from '../../../../sim/src/player/side.ts';
import { gatGameSpeeds, gatGameSpeedsSP, GetSpeedMultiplierString } from '../../ui/gameoptions.ts';
import type { ShellEnv } from '../../ui/shellform.ts';
import { difficultyToHcap, hcapToDifficulty, closestNotch, formatSpeedX } from '../options-io.ts';
import type { Difficulty } from '../menu-controller.ts';

export { difficultyToHcap, hcapToDifficulty };
export type { Difficulty };

// GameOptions.cpp:387 — the scroll slider is 1.0..5.0 in .25 increments.
const knScrollSpeedMax = 5;

/** The in-flight options state held while the form is up — the canvas
 *  InGameOptionsForm's m_* members (ht.h:2920-2936). The view reads/writes these,
 *  then OK / DEFAULT / CANCEL apply them through the env seams. */
export interface GameOptionsModel {
  // ── the game-speed slider (a notch index over the SP/wire table) ──────────────
  /** the current slider notch (an index into the active game-speed table). */
  gameSpeedNotch(): number;
  /** the max notch (table length − 1). */
  gameSpeedNotchMax(): number;
  /** the DEFAULT notch (gameoptions.ts:506 — trunc(kcmsUpdate/20)). */
  defaultGameSpeedNotch(): number;
  /** drag the slider to notch `i` (clamped); held in-flight (no sim change yet). */
  setGameSpeedNotch(i: number): void;
  /** the "N.Nx" label for the current notch (GetSpeedMultiplierString). */
  gameSpeedLabel(): string;

  // ── the scroll-speed slider (1.0..5.0, .25 step → 0..16 notches) ──────────────
  /** the scroll-speed slider value as a 0..16 notch index. */
  scrollSpeedNotch(): number;
  /** the max scroll notch ((knScrollSpeedMax-1)*4 = 16). */
  scrollSpeedNotchMax(): number;
  /** drag the scroll slider to notch `i` (clamped); held in-flight. */
  setScrollSpeedNotch(i: number): void;
  /** the "N.Nx" scroll label for the in-flight value. */
  scrollSpeedLabel(): string;

  // ── difficulty (Easy / Normal / Hard radios) ─────────────────────────────────
  difficulty(): Difficulty;
  setDifficulty(d: Difficulty): void;

  // ── miscellaneous ────────────────────────────────────────────────────────────
  /** Mute Sound checkbox state (= !sndm.IsEnabled()). */
  muted(): boolean;
  /** toggle Mute — LIVE (calls sndm.Enable immediately), like the canvas. */
  setMuted(f: boolean): void;
  /** Mouse Input — the in-flight value. Web no longer forces it: touch-primary devices
   *  (PR #124) default OFF (FingerHandler); the user can toggle it, and OK's SetUIType
   *  swaps the live handler (Stylus when on, Finger when off). */
  mouseMode(): boolean;
  /** toggle Mouse Input — the in-flight value; OK's SetUIType swaps Stylus/Finger. */
  setMouseMode(f: boolean): void;
  /** Lasso Selection — HIDDEN on web; kept for the OK write (always false on web). */
  lassoSelection(): boolean;

  // ── the post-modal actions (canvas OnControlSelected OK/CANCEL/DEFAULT) ────────
  /** OK (GameOptions.cpp:456-494): write lasso/mouse → SetUIType(); setGameSpeed
   *  (the RUNNING sim); options.nScrollSpeed = 1+notch*0.25; options.wfHandicap +
   *  plrLocal.SetHandicap; savePreferences. */
  ok(): void;
  /** CANCEL (GameOptions.cpp:497-500): revert the live mute (sndm.Enable(!original)). */
  cancel(): void;
  /** DEFAULT (GameOptions.cpp:502-510): lasso off, speed kcmsUpdate/20, scroll 1.0,
   *  handicap default, sndm.Enable(true), re-reflect (the view re-renders). */
  setDefaults(): void;
}

/** Build the in-game GameOptions model over a live ShellEnv. Reads the env state
 *  on construction (the canvas Init's m_* capture), then OK / CANCEL / DEFAULT
 *  apply through the SAME env seams the canvas InGameOptionsForm uses. */
export function makeGameOptionsModel(env: ShellEnv): GameOptionsModel {
  // GameOptions.cpp:347-352 — capture the open-time state into the in-flight m_*.
  let mLassoSelection = env.options.fLassoSelection;
  let mMouseMode = env.options.fMouseMode;
  // the ORIGINAL mute, captured for the CANCEL revert (the canvas m_fMuteSound).
  const originalMute = !env.sndm.IsEnabled();
  let mGameSpeed = env.gameSpeed();
  let mWfHandicap = env.options.wfHandicap;
  let mScrollSpeed = env.options.nScrollSpeed;

  // GameOptions.cpp:369-372 (gameSpeedTable): the SP in-game slider ranges over
  // gatGameSpeedsSP (16 notches incl. the 11x SP notch) when a SP game is running;
  // MP / shell keep gatGameSpeeds (the 15-notch wire table). The in-game OPTIONS is
  // only reached on web via the pause menu (DoModalGameOptionsForm), so a game is
  // always running here — this picks the SP table for an SP game, exactly as the
  // canvas form did.
  // The in-game OPTIONS (makeGameOptionsModel) is ONLY reached with a running game, so
  // an SP game uses the 16-notch SP table (incl. the 11x notch). Gate on !fMultiplayer
  // (NOT getSimUIForm(), which can read null while the pause menu holds the form) so the
  // 11x notch is always present in SP — otherwise a game running at 11x (gtGameSpeed=kt11x)
  // isn't in the 15-notch table and the thumb falls to the default notch while the readout
  // still shows 11.0x. The SP table's first 15 entries == gatGameSpeeds, so MP/non-11x are
  // unaffected.
  const gameSpeedTable = (): number[] => (env.fMultiplayer ? gatGameSpeeds : gatGameSpeedsSP);

  // GameOptions.cpp:390-399 — the open notch is m_tGameSpeed's place in the table. Use the
  // shared closestNotch (not exact indexOf): kt11x = 8/11 is a fraction, so float drift in the
  // live gtGameSpeed would miss an exact match and snap the thumb to a wrong default while the
  // label (computed from the value) shows the real speed — the thumb/value mismatch.
  const notchOf = (t: number): number => closestNotch(gameSpeedTable(), t);
  const clampNotch = (i: number, max: number): number => Math.max(0, Math.min(max, Math.round(i)));

  const scrollNotchMax = (): number => (knScrollSpeedMax - 1) * 4; // 16
  const scrollLabel = (v: number): string => formatSpeedX(v);

  return {
    gameSpeedNotch: () => notchOf(mGameSpeed),
    gameSpeedNotchMax: () => gameSpeedTable().length - 1,
    // GameOptions.cpp:506 — the DEFAULT speed is trunc(kcmsUpdate/20).
    defaultGameSpeedNotch: () => notchOf(Math.trunc(kcmsUpdate / 20)),
    setGameSpeedNotch: (i) => {
      const tbl = gameSpeedTable();
      mGameSpeed = tbl[clampNotch(i, tbl.length - 1)];
    },
    gameSpeedLabel: () => GetSpeedMultiplierString(mGameSpeed),

    // GameOptions.cpp:404-405 — the open notch is trunc((nScrollSpeed-1)/0.25).
    scrollSpeedNotch: () => clampNotch((mScrollSpeed - 1.0) / 0.25, scrollNotchMax()),
    scrollSpeedNotchMax: scrollNotchMax,
    // GameOptions.cpp:439 — m_nScrollSpeed = 1.0 + value * 0.25.
    setScrollSpeedNotch: (i) => {
      mScrollSpeed = 1.0 + clampNotch(i, scrollNotchMax()) * 0.25;
    },
    scrollSpeedLabel: () => scrollLabel(mScrollSpeed),

    difficulty: () => hcapToDifficulty(mWfHandicap),
    setDifficulty: (d) => {
      mWfHandicap = difficultyToHcap(d);
    },

    muted: () => !env.sndm.IsEnabled(),
    // GameOptions.cpp:444-445 — Mute toggles sndm.Enable LIVE (immediately).
    setMuted: (f) => {
      env.sndm.Enable(!f);
    },
    mouseMode: () => mMouseMode,
    setMouseMode: (f) => {
      mMouseMode = f;
    },
    lassoSelection: () => mLassoSelection,

    // GameOptions.cpp:456-494 — OK applies everything to the RUNNING game.
    ok: () => {
      env.options.fLassoSelection = mLassoSelection;
      env.options.fMouseMode = mMouseMode;
      // keep the instruction-copy form factor (inputverbs.ts) in step with the
      // live toggle so the E-Com / objective banner / briefing rewrite matches.
      setInstructionMouseMode(mMouseMode);
      // gfLassoSelection || gfMouseMode ? kuitStylus : kuitFinger (SimUI.cpp:219-232);
      // SetUIType() re-picks the active handler from env.options.
      env.getSimUIForm()?.SetUIType();
      // the live sim speed (ggame.SetGameSpeed) — applies to the RUNNING game.
      env.setGameSpeed(mGameSpeed);
      env.options.nScrollSpeed = mScrollSpeed;
      env.options.wfHandicap = mWfHandicap;
      env.plrLocal?.SetHandicap(mWfHandicap);
      // save prefs now in case the game crashes before exiting.
      env.savePreferences();
    },

    // GameOptions.cpp:497-500 — CANCEL reverts the live mute to the open-time state.
    cancel: () => {
      env.sndm.Enable(!originalMute);
    },

    // GameOptions.cpp:502-510 — DEFAULT (re-reflect, does NOT close).
    setDefaults: () => {
      mLassoSelection = false;
      mMouseMode = env.options.fMouseMode; // (sic — the canvas re-reads the global)
      env.sndm.Enable(true);
      mGameSpeed = Math.trunc(kcmsUpdate / 20);
      mScrollSpeed = 1.0;
      mWfHandicap = kfHcapDefault;
    },
  };
}

export { kfHcapEasy, kfHcapNormal, kfHcapHard, kfHcapDefault };
